import React from 'react';
import type { NavSelection } from './types';
import { useCommunityForum } from './CommunityForumContext';
import { useCommunityDevMode } from './CommunityDevModeContext';
import { CommunityDevAdminGate, CommunityDevPath } from './CommunityDevUi';
import { entityRef, P } from './communityDevMode';
import MarketProfileNavCard from './marketProfile/MarketProfileNavCard';
import type { MarketProfileNavTarget } from './marketProfile/marketProfileCta';
import { useAuth } from '../../contexts/AuthContext';

type Props = {
  selection: NavSelection | null;
  onSelectForum: (id: string) => void;
  onSelectChat: (id: string) => void;
  onOpenAdmin: () => void;
  onOpenProfile: (target: MarketProfileNavTarget) => void;
  profileActive: boolean;
  profileTick: number;
  userLabel: string;
  realIsAdmin: boolean;
};

function isForumActive(selection: NavSelection | null, id: string) {
  return selection?.kind === 'forum' && selection.id === id;
}

function isChatActive(selection: NavSelection | null, id: string) {
  return selection?.kind === 'chat' && selection.id === id;
}

export default function CommunitySidebar({
  selection,
  onSelectForum,
  onSelectChat,
  onOpenAdmin,
  onOpenProfile,
  profileActive,
  profileTick,
  userLabel,
  realIsAdmin,
}: Props) {
  const { activeHeaders, getForumsByHeader, getChatsByHeader, isAdmin } = useCommunityForum();
  const { previewAsUser, isDevUser } = useCommunityDevMode();
  const { user } = useAuth();

  const hasNav = activeHeaders.some(
    (h) => getForumsByHeader(h.id).length > 0 || getChatsByHeader(h.id).length > 0,
  );

  return (
    <aside className="dcf-sidebar">
      <div className="dcf-sidebar__brand">
        <div className="dcf-sidebar__brand-title">datacircle</div>
        <div className="dcf-sidebar__brand-sub">Сообщество участников рынка</div>
      </div>

      <div className="dcf-sidebar__profile-entry">
        <MarketProfileNavCard
          active={profileActive}
          userEmail={user?.email}
          profileTick={profileTick}
          onOpen={onOpenProfile}
        />
      </div>

      <nav className="dcf-sidebar__nav" aria-label="Навигация сообщества">
        {activeHeaders.length === 0 ? (
          <p className="px-3 py-2 text-xs text-slate-400">Заголовки не настроены</p>
        ) : (
          activeHeaders.map((header) => {
            const forums = getForumsByHeader(header.id);
            const chats = getChatsByHeader(header.id);
            if (!forums.length && !chats.length) return null;

            const items = [
              ...chats.map((c) => ({ kind: 'chat' as const, sortOrder: c.sortOrder, data: c })),
              ...forums.map((f) => ({ kind: 'forum' as const, sortOrder: f.sortOrder, data: f })),
            ].sort((a, b) => a.sortOrder - b.sortOrder);

            return (
              <div key={header.id} className="dcf-sidebar__group">
                <div className="dcf-sidebar__nav-label">
                  {header.title}
                  <CommunityDevPath path={entityRef(P.headerTitle, header.id)} inline />
                </div>
                {items.map((item) => {
                  if (item.kind === 'chat') {
                    const chat = item.data;
                    return (
                      <div key={chat.id}>
                        <CommunityDevPath path={entityRef(P.chats, chat.id)} />
                        <button
                          type="button"
                          className={`dcf-nav-btn ${isChatActive(selection, chat.id) ? 'dcf-nav-btn--active' : ''}`}
                          onClick={() => onSelectChat(chat.id)}
                        >
                          <span className="dcf-nav-btn__icon dcf-nav-btn__icon--chat">
                            <i className={`fas ${chat.icon}`} aria-hidden="true" />
                          </span>
                          <span>
                            <div className="dcf-nav-btn__name">
                              {chat.name}
                              <CommunityDevPath path={P.chatName} inline />
                            </div>
                            <div className="dcf-nav-btn__meta">
                              <span className="dcf-nav-btn__type">Чат</span>
                              · {chat.messageCount ?? 0} сообщ.
                            </div>
                          </span>
                        </button>
                      </div>
                    );
                  }
                  const forum = item.data;
                  return (
                    <div key={forum.id}>
                      <CommunityDevPath path={entityRef(P.forums, forum.id)} />
                      <button
                        type="button"
                        className={`dcf-nav-btn ${isForumActive(selection, forum.id) ? 'dcf-nav-btn--active' : ''}`}
                        onClick={() => onSelectForum(forum.id)}
                      >
                        <span className="dcf-nav-btn__icon dcf-nav-btn__icon--forum">
                          <i className={`fas ${forum.icon}`} aria-hidden="true" />
                        </span>
                        <span>
                          <div className="dcf-nav-btn__name">
                            {forum.name}
                            <CommunityDevPath path={P.forumName} inline />
                          </div>
                          <div className="dcf-nav-btn__meta">
                            <span className="dcf-nav-btn__type">Форум</span>
                            · {forum.topicCount ?? 0} тем
                          </div>
                        </span>
                      </button>
                    </div>
                  );
                })}
              </div>
            );
          })
        )}
        {!hasNav && activeHeaders.length > 0 && (
          <p className="px-3 py-2 text-xs text-slate-400">Под заголовками пока нет чатов и форумов</p>
        )}
      </nav>

      <div className="dcf-sidebar__footer">
        <CommunityDevAdminGate
          realIsAdmin={realIsAdmin}
          isAdmin={isAdmin}
          label="Управление навигацией"
          storagePath={P.isCommunityAdmin}
        >
          <button type="button" className="dcf-sidebar__admin-link" onClick={onOpenAdmin}>
            <i className="fas fa-sliders" aria-hidden="true" />
            Управление навигацией
          </button>
        </CommunityDevAdminGate>
        <div className="dcf-profile-mini">
          <div className="dcf-profile-mini__avatar">DC</div>
          <div>
            <div className="dcf-profile-mini__name">{userLabel}</div>
            <div className="dcf-profile-mini__meta">
              {isDevUser && previewAsUser ? 'Просмотр: пользователь' : 'Проверенный участник'}
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
}
